import React from 'react';
import { Box, Typography } from '@mui/material';
import styles from './InvoiceViewPaymentModalContent.module.scss';
import InvoiceViewGratuitySelection from './InvoiceViewGratuitySelection';
import { getFormattedCurrency } from '../../../../lib/helpers';
import MuiPaymentMethod from '../../../../mui/MuiPaymentMethod/MuiPaymentMethod';
import PaymentValidationErrorModal from '../../../../mui/MuiPaymentMethod/PaymentValidationErrorModal';
import MuiTextFieldText from '../../../../mui/MuiTextField/MuiTextFieldText/MuiTextFieldText';
import MuiCheckbox from '../../../../mui/MuiCheckbox';
import {
	usePaymentSection,
	usePaymentSectionInvoiceFragment,
} from './usePaymentSection';
import { DocumentNode, gql } from '@apollo/client';
import { InvoiceViewPaymentModalContentInvoiceFragment } from '#types';

const invoiceViewPaymentModalContentInvoiceFragment = gql`
	fragment invoiceViewPaymentModalContentInvoice on Invoice {
		...usePaymentSectionInvoice
	}
	${ usePaymentSectionInvoiceFragment }
`;

export type InvoiceViewPaymentModalContentProps = Omit<
	Parameters<typeof usePaymentSection>[0],
	'invoice'
> & {
	invoice: InvoiceViewPaymentModalContentInvoiceFragment;
	isAnonymousView: boolean;
};

type InvoiceViewPaymentModalContentPropsWithFragment =
	React.FC<InvoiceViewPaymentModalContentProps> & {
		fragments: {
			invoiceViewPaymentModalContentInvoiceFragment: DocumentNode;
		};
	};

export const InvoiceViewPaymentModalContent: InvoiceViewPaymentModalContentPropsWithFragment =
	( { isAnonymousView, ...props } ) => {
		const {
			selectedAmount,
			invoiceTotal,
			shouldRenderGratuityOptions,
			gratuityAmount,
			setGratuityAmount,
			gratuitySelection,
			setGratuitySelection,
			validation,
			cardConvenienceFeePercentage,
			achConvenienceFeePercentage,
			organizationName,
			organizationId,
			paymentMethodType,
			onUpdatePaymentMethodType,
			setLocalStorage,
			sessionSecrets,
			createPaymentIntent,
			abandonPaymentAttempt,
			paymentElementAmount,
			returnUrl,
			contact,
			total,
			loading,
			receiptEmails,
			setReceiptEmails,
			shouldRenderAutoPayOptions,
			autoPaySelection,
			setAutoPaySelection,
			validate,
			hasSubmitted,
			onSuccess,
			multiplePaymentsAtOnce,
			installmentsForValidation,
			guestToken,
			currencyLabel,
			paymentValidationErrorModalOpen,
			onClosePaymentValidationErrorModal,
			sessionPaymentInstallmentId,
		} = usePaymentSection( props );
		return (
			<>
				<PaymentValidationErrorModal
					open={ paymentValidationErrorModalOpen }
					onClose={ onClosePaymentValidationErrorModal }
				/>
				<Box className={ styles.container }>
					<Box
						className={ styles.paymentTotal }
						data-cy='selected-amount-container'
					>
						<Typography
							className={ styles.label }
							data-cy='selected-amount-label'
						>
							Selected amount
						</Typography>
						<Typography variant='h2' data-cy='selected-amount'>
							{ getFormattedCurrency( selectedAmount || 0 ) }
						</Typography>
						<Typography
							variant='caption'
							data-cy='invoice-total-label'
							className={ styles.totalLabel }
						>
							/ { invoiceTotal }
						</Typography>
					</Box>
					{ shouldRenderGratuityOptions ? (
						<InvoiceViewGratuitySelection
							gratuityAmount={ gratuityAmount }
							setGratuityAmount={ setGratuityAmount }
							gratuitySelection={ gratuitySelection }
							setGratuitySelection={ setGratuitySelection }
							validation={ validation?.gratuityValidation }
						/>
					) : null }
					<MuiPaymentMethod
						cardConvenienceFeePercentage={ cardConvenienceFeePercentage }
						achConvenienceFeePercentage={ achConvenienceFeePercentage }
						errorText={ validation?.paymentMethod }
						inputProps={ {
							'data-cy': 'invoice-payment-method',
						} }
						MenuProps={ {
							id: 'invoice-payment-method-menu',
						} }
						submitButtonCopy={ `PAY ${ getFormattedCurrency(
							total
						) }${ currencyLabel }` }
						loading={ loading }
						orgName={ organizationName }
						orgId={ organizationId }
						sessionSecrets={ sessionSecrets }
						deferredPayment={ {
							amount: paymentElementAmount,
							abandonPaymentAttempt,
							createPaymentIntent,
						} }
						returnUrl={ returnUrl }
						onUpdatePaymentMethodType={ onUpdatePaymentMethodType }
						paymentMethodType={ paymentMethodType }
						setLocalStorage={ setLocalStorage }
						handleSuccess={ onSuccess }
						contact={ contact }
						isAnonymousView={ isAnonymousView }
						multiplePaymentsAtOnce={ multiplePaymentsAtOnce }
						installmentsForValidation={ installmentsForValidation }
						guestToken={ guestToken }
						sessionPaymentInstallmentId={ sessionPaymentInstallmentId }
					/>
					<Box className={ styles.row }>
						<Typography
							variant='caption'
							className={ styles.label }
							data-cy='payment-total-label'
						>
							Payment total{ currencyLabel }
						</Typography>
						<Typography variant='h2' data-cy='payment-total-amount'>
							{ getFormattedCurrency( total ) }
						</Typography>
					</Box>
					<MuiTextFieldText
						fullWidth
						label='Notify others of this payment (optional)'
						value={ receiptEmails }
						onChange={ ( e ) => setReceiptEmails( e.target.value ) }
						slotProps={ { input: { 'data-cy': 'receipt-emails' } } }
						errorText={ validation?.receiptEmails }
						helperText='Email, comma separated'
						onBlur={ () => ( hasSubmitted ? validate() : null ) }
					/>
					{ shouldRenderAutoPayOptions ? (
						<Box className={ styles.autoPayContainer }>
							<MuiCheckbox
								slotProps={ { input: { 'data-cy': 'autoPay-checkbox' } } }
								checked={ autoPaySelection }
								onChange={ () => setAutoPaySelection( !autoPaySelection ) }
							/>
							<Typography>
								Turn on autoPay for future installments using the same payment
								method.
							</Typography>
						</Box>
					) : null }
				</Box>
			</>
		);
	};

InvoiceViewPaymentModalContent.fragments = {
	invoiceViewPaymentModalContentInvoiceFragment,
};
